import React, { useMemo } from 'react';
import StackedLaborChart from './StackedLaborChart';

/**
 * Required Labor section: per-process stacked area chart of headcount across
 * the planning intervals. Rows from the cycle's grid data become series; the
 * sum across rows becomes the dashed Total line.
 */
const RequiredLaborChart = ({ intervals, gridCycleData }) => {
  const { requiredSeries, requiredTotal } = useMemo(() => {
    const rows = gridCycleData?.rows;
    if (!rows?.length || !intervals?.length) return { requiredSeries: [], requiredTotal: [] };
    const series = rows
      .filter((r) => r.key !== 'STOW_WIP')
      .map((row) => {
        const data = intervals.map((iv, i) => ({
          x: iv.startLabel,
          y: Math.round((row.slots?.[i]?.headcount || 0) * 10) / 10
        }));
        data.push({
          x: intervals[intervals.length - 1].endLabel,
          y: Math.round((row.slots?.[intervals.length - 1]?.headcount || 0) * 10) / 10
        });
        return { key: row.key, label: row.displayName, data };
      });
    const numPts = series[0]?.data.length || 0;
    const total = Array.from({ length: numPts }, (_, i) => ({
      x: series[0].data[i].x,
      y: series.reduce((sum, s) => sum + (s.data[i]?.y || 0), 0)
    }));
    return { requiredSeries: series, requiredTotal: total };
  }, [gridCycleData, intervals]);

  // Interval-only categories — Required Labor's x-axis doesn't need cohort
  // boundaries, only the planning interval start/end times.
  const requiredCategories = useMemo(() => {
    if (!intervals?.length) return [];
    const seen = new Set();
    const cats = [];
    intervals.forEach((iv) => {
      [iv.startLabel, iv.endLabel].forEach((l) => {
        if (l && !seen.has(l)) {
          seen.add(l);
          cats.push(l);
        }
      });
    });
    return cats;
  }, [intervals]);

  if (!requiredSeries.length) return null;

  return (
    <StackedLaborChart
      title="Required Labor"
      series={requiredSeries}
      totalData={requiredTotal}
      intervals={intervals}
      categories={requiredCategories}
    />
  );
};

export default RequiredLaborChart;
